import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Pagination,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import useDebounce from "../hooks/useDebounce";
import normalizarTexto from "../utils/normalizarTexto";

const ITEMS_POR_PAGINA = 8;

const DataFilter = ({categoriasList, categoriaIgual1, categoriaIgual2, categoriaIgual3, dataItem, dataBusqueda, labelBusqueda, labelSelect, ComponentView, GridXs, GridSm, GridMd, GridLg}) => {
  const [categoria, setCategoria] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState("az"); // az | za
  const [pagina, setPagina] = useState(1);

  const busquedaDebounced = useDebounce(busqueda, 300);

  // 🔍 FILTRADO + BÚSQUEDA + ORDEN
  const datosFiltrados = useMemo(() => {
    const texto = normalizarTexto(busquedaDebounced);

    const filtrados = dataItem.filter((e) => {
      const coincideCategoria =
        !categoria ||
        e[categoriaIgual1] === categoria ||
        e[categoriaIgual2] === categoria ||
        e[categoriaIgual3] === categoria;

      const coincideBusqueda =
        normalizarTexto(e[dataBusqueda])?.includes(texto)
        || normalizarTexto(e[categoriaIgual1])?.toLowerCase().includes(texto) ||
        (e[categoriaIgual2])?.toLowerCase().includes(texto) ||
        (e[categoriaIgual3])?.toLowerCase().includes(texto);

      return coincideCategoria && coincideBusqueda;
    });

    // 🔠 ORDENAMIENTO
    filtrados.sort((a, b) => {
      if (orden === "az") {
        return a[dataBusqueda].localeCompare(b[dataBusqueda]);
      }
      return b[dataBusqueda].localeCompare(a[dataBusqueda]);
    });
    return filtrados;
  }, [dataItem, categoriaIgual1, categoriaIgual2, categoriaIgual3, dataBusqueda, categoria, busquedaDebounced, orden]);

  const categoriasOrdenadas = useMemo(() => {
    if (!categoriasList) return [];
    return [...categoriasList].sort((a,b) => a.localeCompare(b));
  },[categoriasList]);

  // 📄 PAGINACIÓN
  const totalPaginas = Math.ceil(
    datosFiltrados.length / ITEMS_POR_PAGINA
  );

  const dataPaginados = datosFiltrados.slice(
    (pagina - 1) * ITEMS_POR_PAGINA,
    pagina * ITEMS_POR_PAGINA
  );

  return (
    <Box sx={{ py: 4 }}>
      <Box
        sx={{
          // width: "1300px",
          // maxWidth: "1300px",
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <TextField
          label={labelBusqueda}
          size="small"
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setPagina(1);
          }}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{labelSelect}</InputLabel>
          <Select
            label={labelSelect}
            value={categoria}
            onChange={(e) => {
              setCategoria(e.target.value);
              setPagina(1);
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {categoriasOrdenadas.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Ordenar</InputLabel>
          <Select
            label="Ordenar"
            value={orden}
            onChange={(e) => {
              setOrden(e.target.value);
              setPagina(1);
            }}
          >
            <MenuItem value="az">Nombre A–Z</MenuItem>
            <MenuItem value="za">Nombre Z–A</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={() => {
            setCategoria("");
            setBusqueda("");
            setOrden("az");
            setPagina(1);
          }}
        >
          Limpiar filtros
        </Button>
      </Box>

      {/* CONTADOR */}
      <Typography
        variant="subtitle1"
        color="text.secondary"
        textAlign="center"
        mb={3}
      >
        {datosFiltrados.length === 0
          ? "No se encontraron resultados"
          : `${datosFiltrados.length} registro${
              datosFiltrados.length !== 1 ? "s" : ""
            } encontrado${
              datosFiltrados.length !== 1 ? "s" : ""
            }`}
      </Typography>

      {/* LISTADO */}
      <Grid container spacing={2} sx={{justifySelf:"center", maxWidth:"1400px", padding:"0 12px"}}>
        {dataPaginados.length === 0 ? (
          <Grid item xs={GridXs} textAlign="center">
            No hay registros disponibles.
          </Grid>
        ) : (
          dataPaginados.map((dataObject) => (
            <Grid item xs={GridXs} sm={GridSm} md={GridMd} lg={GridLg} key={dataObject.id}>
              <ComponentView contenido={dataObject} />
            </Grid>
          ))
        )}
      </Grid>

      {/* PAGINACIÓN */}
      {totalPaginas > 1 && (
        <Box mt={4} display="flex" justifyContent="center">
          <Pagination
            count={totalPaginas}
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default DataFilter;
